<template>
  <SetupCard @keydown.esc="!adding && emit('cancel')">
    <h2 ref="titleEl" class="setupTitle" tabindex="-1" :aria-busy="loading || undefined">{{ title }}</h2>

    <div v-if="loadError" class="failRow">
      <AppIcon name="error" :size="16" color="var(--color-danger)" /><span>{{ loadError }}</span>
    </div>
    <p v-else-if="!loading && !leads.length" class="setupCopy">
      Capture people in the LeadIQ extension and they’ll show up here with their mobile numbers.
    </p>
    <template v-else-if="leads.length">
      <p class="setupCopy">Pick who to call. Everyone you captured with a mobile number is here, newest first.</p>
      <CapturedLeadsList
        :leads="leads"
        :selected-count="selectedCount"
        :all-selected="allSelected"
        :disabled="adding"
        @toggle-lead="emit('toggle-lead', $event)"
        @toggle-all="emit('toggle-all')"
      />
      <p v-if="!hasAddable" class="setupNote">Everyone you captured recently is already in your queue.</p>
      <div v-if="addError" class="failRow">
        <AppIcon name="error" :size="16" color="var(--color-danger)" /><span>{{ addError }}</span>
      </div>
    </template>

    <div class="actions">
      <template v-if="loadError">
        <button type="button" class="linkBtn" @click="emit('cancel')">Cancel</button>
        <button type="button" class="btn secondary" @click="emit('retry')">Try again</button>
      </template>
      <button v-else-if="!loading && !leads.length" type="button" class="btn secondary" @click="emit('cancel')">Back to my queue</button>
      <template v-else>
        <button type="button" class="linkBtn" :disabled="adding" @click="emit('cancel')">Cancel</button>
        <button v-if="leads.length" type="button" class="btn primary" :disabled="selectedCount === 0 || adding" @click="emit('add')">
          {{ addLabel }}
        </button>
      </template>
    </div>
  </SetupCard>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import AppIcon from '../../../components/AppIcon.vue'
import CapturedLeadsList from './CapturedLeadsList.vue'
import SetupCard from './SetupCard.vue'
import type { SelectableLead } from '../../composables/useCapturedLeads'

const props = defineProps<{
  leads: SelectableLead[]
  selectedCount: number
  allSelected: boolean
  hasAddable: boolean
  loading: boolean
  loadError: string | null
  adding: boolean
  addError: string | null
}>()

const emit = defineEmits<{
  (e: 'toggle-lead', leadId: string): void
  (e: 'toggle-all'): void
  (e: 'add'): void
  (e: 'cancel'): void
  (e: 'retry'): void
}>()

// The page swapped under the keyboard, so land focus on the card.
const titleEl = ref<HTMLElement | null>(null)
onMounted(() => titleEl.value?.focus())

// One heading: the source. The state lives in the copy beneath it.
const title = computed(() => {
  if (props.loading) return 'Finding your recent captures…'
  if (props.loadError) return 'Couldn’t read your LeadIQ captures'
  if (!props.leads.length) return 'No recent LeadIQ captures'
  return 'Recent LeadIQ captures'
})

const addLabel = computed(() =>
  props.adding ? 'Adding…' : props.selectedCount > 0 ? `Add ${props.selectedCount} to my call list` : 'Add to my call list',
)
</script>
